import Ajv2020, { type ErrorObject } from "ajv/dist/2020.js";
import reviewSceneSchema from "../../../contracts/review_scene.schema.json";
import type {
  Asset as GeneratedReviewSceneAsset,
  Bounds as GeneratedMillimetreBounds,
  CameraPreset as GeneratedCameraPreset,
  ExclusionVolume as GeneratedHardExclusionVolume,
  Layer as GeneratedReviewSceneLayer,
  PlacementSurface as GeneratedPlacementSurface,
  ReviewHotspot as GeneratedReviewHotspot,
  ShedReviewScene,
  StudMountLine as GeneratedStudMountLine,
  Vector3,
} from "../generated/review-scene";

export type MillimetrePoint = Vector3;
export type ReviewSceneAsset = GeneratedReviewSceneAsset;
export type ReviewSceneLayer = GeneratedReviewSceneLayer;

export type ReviewLayerId = "shell" | "core_interior";
export type CameraPreset = GeneratedCameraPreset;
export type MillimetreBounds = GeneratedMillimetreBounds;
export type PlacementSurface = GeneratedPlacementSurface;
export type StudMountLine = GeneratedStudMountLine;
export type HardExclusionVolume = GeneratedHardExclusionVolume;
export type ReviewHotspot = GeneratedReviewHotspot;
export type ReviewScene = Omit<
  ShedReviewScene,
  | "camera_presets"
  | "placement_surfaces"
  | "stud_mount_lines"
  | "hard_exclusion_volumes"
  | "review_hotspots"
> & {
  camera_presets: CameraPreset[];
  placement_surfaces: PlacementSurface[];
  stud_mount_lines: StudMountLine[];
  hard_exclusion_volumes: HardExclusionVolume[];
  review_hotspots: ReviewHotspot[];
};

export class ReviewSceneValidationError extends Error {
  readonly validationErrors: ErrorObject[];

  constructor(errors: ErrorObject[] = []) {
    const detail = errors
      .slice(0, 3)
      .map((error) => `${error.instancePath || "/"} ${error.message ?? "is invalid"}`)
      .join("; ");
    super(`review_scene.json does not match schema version 1${detail ? `: ${detail}` : ""}`);
    this.name = "ReviewSceneValidationError";
    this.validationErrors = errors;
  }
}

const ajv = new Ajv2020({ allErrors: true, strict: true });
const validateReviewScene = ajv.compile<ReviewScene>(reviewSceneSchema);

export function parseReviewScene(value: unknown): ReviewScene {
  if (!validateReviewScene(value)) {
    throw new ReviewSceneValidationError(validateReviewScene.errors ?? []);
  }
  return value;
}

export async function loadReviewScene(url: URL, signal?: AbortSignal): Promise<ReviewScene> {
  const response = await fetch(url, {
    headers: { Accept: "application/json" },
    ...(signal ? { signal } : {}),
  });
  if (!response.ok) {
    throw new Error(`Review scene request failed with HTTP ${response.status}`);
  }
  return parseReviewScene(await response.json());
}

export function findCameraPreset(scene: ReviewScene, id: string): CameraPreset {
  const preset = scene.camera_presets.find((candidate) => candidate.id === id);
  if (!preset) throw new Error(`Review scene does not define the '${id}' camera preset`);
  return preset;
}

export function defaultCameraPreset(scene: ReviewScene): CameraPreset {
  const presets = scene.camera_presets.filter((preset) => preset.is_default);
  if (presets.length !== 1) {
    throw new Error(`Review scene must define exactly one default camera; found ${presets.length}`);
  }
  return presets[0]!;
}

export function assetUrl(manifestUrl: URL, asset: ReviewSceneAsset): URL {
  return new URL(asset.path, manifestUrl);
}
